import React, { useContext, useState } from 'react';
import {
  ContractStatus,
  Phase3,
  AgFinancingDocumentType,
  AgFinancingFilesContext,
  AgFinancingFile
} from '@indigo-ag/schema';
import { Select, Button, useFlashMessage, Textarea } from '@indigo-ag/component-module';
import { faCloudUpload } from '@fortawesome/pro-regular-svg-icons';
import styled from 'styled-components';
import { Dictionary } from 'lodash';

import i18next from 'core/i18n';
import { PhaseSection, documentRequestTypesOptions, UploadDocumentCard, AttachButton } from 'shared';
import { ProgressDictionary } from 'features/brazil-grower/components/BrazilGrowerFinance/utils';
import { CollateralFormalizationContext } from '../../CollateralAnalysis/CollateralFormalization/CollateralFormalization';

const StyledSelect = styled(Select)`
  margin-bottom: 24px;
  margin-right: 20px;
  max-height: 54px;
  width: 180px;
`;

interface SignDigitallySectionProps {
  phase3Info: Phase3;
}

export const SignDigitallySection: React.FunctionComponent<SignDigitallySectionProps> = ({ phase3Info }) => {
  /**
   * State
   */
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [documentTypeDictionary, setUpdateDocumentType] = useState<Dictionary<AgFinancingDocumentType>>({});
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<ProgressDictionary>();
  const [notes, setNotes] = useState('');

  /**
   * Context
   */
  const { updatePhase3, uploadFilesToPhase3 } = useContext(CollateralFormalizationContext);

  const { addFlashMessage } = useFlashMessage();

  const handleFilesSelected = (files: File[]) => {
    setSelectedFiles(files);
  };

  const handleFileRemove = (fileId: string) => {
    setSelectedFiles(selectedFiles.filter(f => f['newFileId'] !== fileId));
  };

  const handleUpdateDocumentType = (fileId: string) => {
    return (newDocumentType: string | null) => {
      if (newDocumentType) {
        setUpdateDocumentType({ ...documentTypeDictionary, [fileId]: newDocumentType as AgFinancingDocumentType });
      }
    };
  };

  const handleNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setNotes(event.target.value);
  };

  const hanldeSignDigitally = () => {
    setSaving(true);
    uploadFilesToPhase3(
      selectedFiles,
      AgFinancingFilesContext.AMENDMENT_SIGN_DOCUMENTS,
      documentTypeDictionary,
      percentCompleted => setProgress({ ...percentCompleted })
    )
      .then(filesUploaded =>
        updatePhase3(
          {
            contractStatus: ContractStatus.REVIEWING_SIGNED_DOCUMENTS,
            amendmentSignedNotes: notes
          },
          filesUploaded.fulfilled as AgFinancingFile[]
        )
      )
      .then(() => {
        addFlashMessage({
          content: `${i18next.t('amendmentInformationSavedSuccessfully')}`,
          title: '',
          variant: 'success'
        });
        //
      })
      .catch(() => {
        addFlashMessage({
          content: `${i18next.t('error')}`,
          title: '',
          variant: 'error'
        });
        setSaving(false);
      });
  };

  return (
    <PhaseSection title={i18next.t('signingDigitalDocuments')} classes={{ card: 'mt-4' }}>
      <>
        <Textarea
          label={`${i18next.t('commentsOrRecommendations')} (${i18next.t('optional')})`}
          rows={4}
          value={notes}
          onChange={handleNotesChange}
        />
        {!!selectedFiles.length && (
          <div className="flex flex-column">
            {selectedFiles.map(file => (
              <div className="flex align-items-center" key={file['newFileId']}>
                <StyledSelect
                  key={file['newFileId']}
                  sizeVariant="slim"
                  label={i18next.t('documentType')}
                  name="documentType"
                  options={documentRequestTypesOptions}
                  value={documentTypeDictionary[file['newFileId']]}
                  onSelectionChanged={handleUpdateDocumentType(file['newFileId'])}
                  placeholder={i18next.t('pleaseSelect')}
                  error={!documentTypeDictionary[file['newFileId']]}
                />
                <div style={{ maxWidth: 'calc(100% - 200px)' }}>
                  <UploadDocumentCard
                    documentName={file.name}
                    progress={progress ? progress[file['newFileId']] : undefined}
                    onRemove={() => handleFileRemove(file['newFileId'])}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-space-btw">
          <AttachButton
            onFilesSelected={handleFilesSelected}
            size="small"
            faIconAfter={faCloudUpload}
            variant="tertiary"
          >
            {i18next.t('uploadFiles')}
          </AttachButton>
          <Button
            size="small"
            className="text-transform-capitalize"
            disabled={!selectedFiles.length || selectedFiles.some(f => !documentTypeDictionary[f['newFileId']])}
            onClick={hanldeSignDigitally}
            busy={saving}
          >
            {i18next.t('submit')}
          </Button>
        </div>
      </>
    </PhaseSection>
  );
};
